import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import BundleCard from './BundleCard';
import BundleDetail from './BundleDetail';
import { buildFontFaceCss } from './NumberPreviewThumb';
import { playSuccess, playError } from './sounds';
import {
    getAttr,
    getPressingItems,
    getReferenceOnlyItems,
    computeBundleStates,
    getGroupPlayerName,
    getGroupTeamName,
    EMBELLISHMENT_TYPES,
} from './pressingHelpers';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import { useT } from './i18n/I18nContext';
import VirtualCardList from './VirtualCardList';
import ManagerOverrideModal from './ManagerOverrideModal';
import ManagerOverrideBanner from './ManagerOverrideBanner';
import { MissingEmbellishmentsKiosk } from './MissingEmbellishments';
import CameraScanner from './CameraScanner';
import { synthesizeJumbledBundleNotifications } from './jumbledBundleUtils';

// A scanned string is treated as a Manager Override barcode iff it matches
// this shape: `OH-` prefix + 15 digits. Shared with picking + packing.
export const MANAGER_BARCODE_RX = /^OH-\d{15}$/;

// Three-column kanban board with drag-and-drop (Task 11).
// Props mirror what ItemCycler received so the parent swap is drop-in.
//
//   orderId, orderShopifyLocationId, orderStoreKey, fetchFn, onBack, presserName

function DraggableCard({ gid, group, pickStatus, onClick, scanOnly, overrideActive, skuNotification, tpOverridesByName }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: gid });
    return (
        <BundleCard
            group={group}
            pickStatus={pickStatus}
            onClick={onClick}
            scanOnly={scanOnly}
            overrideActive={overrideActive}
            dragRef={setNodeRef}
            dragAttrs={attributes}
            dragListeners={listeners}
            isDragging={isDragging}
            skuNotification={skuNotification}
            tpOverridesByName={tpOverridesByName}
        />
    );
}

// DroppableColumn renders a header, optional non-scrolling top region (header
// addons like the scan input), a virtualized scrolling card area, and an
// optional non-scrolling bottom region (e.g. reference items). The virtualized
// area is what mounts/unmounts cards as the column scrolls.
//
// Props:
//   id, className, title, count: same as before
//   topAddon   — node rendered above the scroll area (sticky-feel, not virtual)
//   items      — array passed to VirtualCardList
//   getKey     — VirtualCardList getKey
//   renderItem — VirtualCardList renderItem
//   footer     — node rendered below the scroll area (reference items, etc.)
//   estimateSize — px estimate for VirtualCardList (default 220)
function DroppableColumn({
    id, className, title, count,
    topAddon, items, getKey, renderItem, footer,
    estimateSize = 220,
}) {
    const { setNodeRef, isOver } = useDroppable({ id });
    // Per-item estimate: reference cards are locked to 300px in CSS
    // (`.ppb__ref-card-wrap`). Feeding that back to the virtualizer up-front
    // avoids offset drift while measurement catches up — the drift caused
    // reference tiles to overlap.
    const estimateFn = (index) => {
        const it = items[index];
        if (it && it.kind === 'ref') return 400;
        return typeof estimateSize === 'function' ? estimateSize(index) : estimateSize;
    };
    return (
        <div ref={setNodeRef}
             className={`${className}${isOver ? ' ppb__col--over' : ''}`}>
            <div className="ppb__col-hdr">
                <span className="ppb__col-title">{title}</span>
                <span className="ppb__col-count">{count}</span>
            </div>
            {topAddon}
            <VirtualCardList
                className="ppb__cards"
                items={items}
                getKey={getKey}
                renderItem={renderItem}
                estimateSize={estimateFn}
                overscan={6}
                gap={10}
            />
            {footer}
        </div>
    );
}

// Strip !Player#N / #N / |suffix suffixes to get the base SKU for require_scanning lookup.
// Mirrors the server-side regex: re.sub(r'[!#|].*$', '', group_key)
function getBaseSku(sku) {
    if (!sku) return '';
    const m = sku.search(/[!#|]/);
    return m === -1 ? sku : sku.slice(0, m);
}

export default function PressingOrderBoard({
    orderId, orderShopifyLocationId, orderStoreKey, fetchFn, onBack, presserName,
    onActiveNotifications,
    notifications = [], onReviewNotifications = null,   // 2026-09-02: header "Review Notification" button (matches Packing)
}) {
    const t = useT();
    const [order, setOrder]             = useState(null);
    // SKU Tools: {sku: message} for every SKU on this order with an active
    // notification. Passed into BundleCard for the red badge on tiles.
    const [skuNotifications, setSkuNotifications] = useState({});
    useEffect(() => {
        if (!orderId) return;
        fetchFn(`/api/sku-notifications/for-order/${orderId}`, undefined, orderStoreKey)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
    }, [orderId, orderStoreKey, fetchFn]);
    const [groups, setGroups]           = useState([]);
    // Reference-only items: line items at this location that don't need
    // pressing. Surfaced at the bottom of the unpressed column so pressers
    // see the full order contents. Never count toward the press percentage.
    const [referenceItems, setReferenceItems] = useState([]);
    const [portalState, setPortalState] = useState({});
    const [numberPreviews, setNumberPreviews] = useState({});
    const [sortMode, setSortMode]       = useState('product'); // 'product' | 'player'
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState('');
    // gid → 'unpressed' | 'pressing' | 'pressed'
    const [columnByGid, setColumnByGid] = useState({});
    const [moveOrder, setMoveOrder] = useState([]);
    const [detailGid, setDetailGid] = useState(null);
    // 'scan' (parent barcode scanned into ppb__scan-input) vs 'manual' (tapped card)
    const detailMethodRef = useRef('manual');
    const openDetail = useCallback((gid, method) => {
        detailMethodRef.current = method || 'manual';
        setDetailGid(gid);
    }, []);
    const detailGroup = detailGid ? groups.find(g => g.gid === detailGid) : null;
    const [scanInput, setScanInput] = useState('');
    const [overScanModal, setOverScanModal] = useState(null); // { kind: 'over' | 'not_in_order', title, variant, sku, barcode, totalCount }
    // Manager Override — order-wide, time-limited (60s from verify).
    //   managerOverride = { manager_user_id, first_name, last_name,
    //                       manager_name, expiresAt } | null
    // While active, every scan-only-blocked tile on this order becomes
    // tappable and the whole board turns pastel blue as a visual cue.
    // Auto-clears when expiresAt passes; also cleared on order switch.
    const MANAGER_OVERRIDE_MS = 60 * 1000;
    const [pendingMgrBarcode, setPendingMgrBarcode] = useState(null);
    const [managerOverride, setManagerOverride]     = useState(null);
    const [cameraOpen, setCameraOpen]               = useState(false);
    const [_mgrNow, _setMgrNow]                     = useState(() => Date.now());
    useEffect(() => {
        if (!managerOverride) return;
        const id = setInterval(() => _setMgrNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [managerOverride]);
    useEffect(() => {
        if (managerOverride && _mgrNow > managerOverride.expiresAt) {
            setManagerOverride(null);
        }
    }, [_mgrNow, managerOverride]);
    const managerActive = !!(managerOverride && _mgrNow < managerOverride.expiresAt);
    useEffect(() => {
        setManagerOverride(null);
        setPendingMgrBarcode(null);
    }, [orderId]);
    const [flash, setFlash] = useState(null); // 'green' | 'red' | null
    const scanRef = useRef(null);

    const triggerFlash = useCallback((color) => {
        setFlash(color);
        setTimeout(() => setFlash(null), 600);
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchFn(`/api/orders/${orderId}`, undefined, orderStoreKey)
            .then(r => r.json())
            .then(data => {
                setOrder(data);
                if (onActiveNotifications) {
                    const _pending = [
                        ...synthesizeJumbledBundleNotifications(
                            data?.jumbled_bundles, data?.name || data?.order_name),
                        ...(Array.isArray(data?.active_notifications) ? data.active_notifications : []),
                    ];
                    if (_pending.length) onActiveNotifications(_pending);
                }
                const g = getPressingItems(data, orderShopifyLocationId);
                setGroups(g);
                setReferenceItems(getReferenceOnlyItems(data, orderShopifyLocationId));
                fetchFn(`/api/orders/${orderId}/portal-state`, undefined, orderStoreKey)
                    .then(r => r.ok ? r.json() : {})
                    .then(state => {
                        setPortalState(state);
                        // Seed columns: pressed if qty_pressed > 0, else unpressed.
                        // (Pressing column was removed — clicking Unpressed opens detail directly.)
                        const initial = {};
                        const bundleStates = computeBundleStates(g, state);
                        g.forEach((grp, i) => {
                            initial[grp.gid] = bundleStates[i].isPressed ? 'pressed' : 'unpressed';
                        });
                        setColumnByGid(initial);
                    })
                    .catch(() => setError(t('Failed to load portal state.')));
            })
            .catch(() => setError(t('Failed to load order details.')))
            .finally(() => setLoading(false));

        fetchFn(`/api/orders/${orderId}/number-previews`, undefined, orderStoreKey)
            .then(r => r.ok ? r.json() : { previews: {} })
            .then(d => setNumberPreviews(d.previews || {}))
            .catch(() => setNumberPreviews({}));
    }, [orderId, orderShopifyLocationId, orderStoreKey, fetchFn]);

    const bundleStates = useMemo(
        () => computeBundleStates(groups, portalState),
        [groups, portalState]
    );
    const stateByGid = useMemo(() => {
        const m = {};
        groups.forEach((g, i) => { m[g.gid] = bundleStates[i]; });
        return m;
    }, [groups, bundleStates]);

    // Sort
    const sortedGroups = useMemo(() => {
        if (sortMode === 'player') {
            return [...groups].sort((a, b) =>
                getGroupPlayerName(a).localeCompare(getGroupPlayerName(b))
            );
        }
        return groups; // product = original Shopify order
    }, [groups, sortMode]);

    const byMoveOrder = useCallback((a, b) => {
        const ai = moveOrder.indexOf(a);
        const bi = moveOrder.indexOf(b);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    }, [moveOrder]);

    const gidsInColumn = useCallback((col) =>
        sortedGroups.filter(g => columnByGid[g.gid] === col).map(g => g.gid).sort(byMoveOrder),
        [sortedGroups, columnByGid, byMoveOrder]);

    const unpressedGids = gidsInColumn('unpressed');
    const pressedGids   = gidsInColumn('pressed');

    // Sensors:
    //   MouseSensor — desktop. 5px distance so cards grab easily with a mouse.
    //   TouchSensor — iPad. 150ms hold + 5px tolerance = "tap and hold to grab",
    //     the canonical iOS drag gesture. Without this, the default PointerSensor
    //     steals touches from scroll AND makes grabbing feel broken.
    //   PointerSensor is deliberately omitted — MouseSensor+TouchSensor together
    //     cover both input types more reliably than PointerSensor on iOS.
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 150, tolerance: 5 },
        }),
    );
    const [activeDragGid, setActiveDragGid] = useState(null);

    const getPresserSession = () => {
        try { return JSON.parse(sessionStorage.getItem('presser_session') || 'null'); }
        catch { return null; }
    };

    // Scan-only gate: presser flag + per-SKU require_scanning lookup.
    // Both are derived outside the render loop for perf; the set lookup is O(1).
    const userScanOnly = useMemo(() => {
        const s = getPresserSession();
        return !!(s?.scan_only_presser);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const requireScanningBySku = useMemo(
        () => order?._cache?.require_scanning_by_sku || {},
        [order]
    );

    // TP number overrides keyed by player name — shared between the board's
    // BundleCards (unpressed + pressed columns + drag overlay) and the
    // BundleDetail modal. Any card whose player name matches gets the
    // RDS number highlighted in yellow instead of the checkout-embedded one.
    const tpOverridesByName = useMemo(() =>
        ((order?.tp_number_overrides) || []).reduce((m, ov) => {
            if (ov && ov.player_name) m[ov.player_name] = ov;
            return m;
        }, {}),
    [order?.tp_number_overrides]);

    const postBundleEvent = useCallback((eventType, gid, extra = {}) => {
        const groupIdx   = groups.findIndex(g => g.gid === gid);
        const state      = bundleStates[groupIdx];
        if (!state?.groupKey) return;
        const session = getPresserSession();
        const identity = {
            order_name:          order?.replacement_alias || order?.name,
            shopify_location_id: orderShopifyLocationId,
            portal_userid:       session?.presser_id || null,
            portal_username:     session
                ? `${session.first_name} ${session.last_name}`
                : (presserName || null),
        };
        // Manager override attribution — active while the 60s window is
        // open; applied to every event posted during that window on this
        // order so the audit log shows who authorized the manual pass.
        if (managerActive && managerOverride) {
            identity.manager_override = {
                user_id:    managerOverride.manager_user_id,
                first_name: managerOverride.first_name,
                last_name:  managerOverride.last_name,
                name:       managerOverride.manager_name,
            };
        }

        // Bundle finish side-effect: auto-mark the parent + all embellishments as picked
        let embellishment_picks;
        let unpick_candidates;
        if (eventType === 'bundle_finish') {
            embellishment_picks = [
                { group_key: state.groupKey, qty_picked: state.skuInstanceIndex, qty_total: state.skuInstanceIndex },
                ...(state.embPickStates || [])
                    .filter(e => e.embSku)
                    .map(e => ({ group_key: e.embSku, qty_picked: e.embIdx, qty_total: e.embIdx })),
            ];
        } else if (eventType === 'bundle_unpress') {
            // Backend resets qty_picked = 0 for any of these group_keys that have
            // no picking-portal events (i.e. the pick came from an earlier press).
            unpick_candidates = [
                state.groupKey,
                ...(state.embPickStates || []).filter(e => e.embSku).map(e => e.embSku),
            ];
        }

        const payload = {
            ...identity,
            group_key:    state.groupKey,
            // bundle_index MUST be the per-SKU instance index used by the read
            // side (computeBundleStates → skuInstanceIndex). It used to be the
            // 0-based groupIdx across all bundles, which silently mismatched
            // and made press_instances["0"] saved but the renderer looked up
            // press_instances["1"] — pressing one bundle did nothing on reopen.
            bundle_index: state.skuInstanceIndex,
            qty_before:   portalState[state.groupKey]?.qty_pressed || 0,
            qty_after:    eventType === 'bundle_finish' ? state.skuInstanceIndex
                        : eventType === 'bundle_unpress' ? Math.max(0, state.skuInstanceIndex - 1)
                        : (portalState[state.groupKey]?.qty_pressed || 0),
            event_type:   eventType,
            embellishment_picks,
            unpick_candidates,
            // Capture the moment the presser actually clicked Finish — backend
            // uses this as the event's created_at instead of NOW(), so the
            // Pressing Times report can measure real per-bundle duration.
            finished_at:  new Date().toISOString(),
            ...extra,
        };
        fetchFn(`/api/pressing/orders/${orderId}/bundle-event`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ ...payload, event_source: extra.event_source || 'manual' }),
        }, orderStoreKey).catch(() => {});

        if (eventType === 'bundle_finish') {
            setPortalState(prev => {
                const prevPI = prev[state.groupKey]?.press_instances || {};
                const newPI  = { ...prevPI, [String(state.skuInstanceIndex)]: { pressed: true } };
                const newQty = Object.values(newPI).filter(v => v?.pressed).length;
                const next = {
                    ...prev,
                    [state.groupKey]: {
                        ...prev[state.groupKey],
                        press_instances: newPI,
                        qty_pressed:     newQty,
                    },
                };
                for (const e of (embellishment_picks || [])) {
                    next[e.group_key] = { ...prev[e.group_key], qty_picked: Math.max(prev[e.group_key]?.qty_picked || 0, e.qty_picked) };
                }
                return next;
            });
        } else if (eventType === 'bundle_unpress') {
            setPortalState(prev => {
                const prevPI = prev[state.groupKey]?.press_instances || {};
                const { [String(state.skuInstanceIndex)]: _drop, ...newPI } = prevPI;
                const newQty = Object.values(newPI).filter(v => v?.pressed).length;
                return {
                    ...prev,
                    [state.groupKey]: {
                        ...prev[state.groupKey],
                        press_instances: newPI,
                        qty_pressed:     newQty,
                    },
                };
            });
            // Backend decides which candidates to revert (only those without any
            // picking-portal events). Refetch to pull the authoritative pick state.
            fetchFn(`/api/orders/${orderId}/portal-state`, undefined, orderStoreKey)
                .then(r => r.ok ? r.json() : null)
                .then(s => { if (s) setPortalState(s); })
                .catch(() => {});
        }
    }, [groups, bundleStates, order, orderShopifyLocationId, orderStoreKey, portalState, fetchFn, orderId, presserName]);

    const handleDragStart = (event) => {
        setActiveDragGid(event.active.id);
    };
    const handleDragEnd = (event) => {
        setActiveDragGid(null);
        const { active, over } = event;
        if (!over) return;
        const gid    = active.id;
        const target = over.id;
        const fromCol = columnByGid[gid];
        if (target === fromCol) return;

        // Pressed column is drop-forbidden
        if (target === 'pressed') return;

        // Moving OUT of Pressed back to Unpressed — fire bundle_unpress
        if (fromCol === 'pressed' && target === 'unpressed') {
            postBundleEvent('bundle_unpress', gid);
        }

        setColumnByGid(prev => ({ ...prev, [gid]: target }));
        setMoveOrder(prev => [gid, ...prev.filter(k => k !== gid)]);
    };

    // Order metadata for the header — use exact-key lookup, not includes('club') which
    // can match a config blob attribute and spam the header.
    const orderClub = getAttr(order?.customAttributes, 'Club', 'club', 'club_name', 'Team Club', 'team_club') || '';

    // Collect unique team names and player names across all bundles in the order.
    const teamNames = [...new Set(groups.map(getGroupTeamName).filter(Boolean))];
    const playerNames = [...new Set(groups.map(getGroupPlayerName).filter(Boolean))];

    // Match helper — returns every bundle whose parent item barcode or sku equals
    // the scan (case-insensitive, trimmed). Embellishments never count as the parent.
    // Phase 3C: also matches when the scan equals the NEW SKU of an active swap
    // whose OLD SKU is the parent's SKU (so scanning a swapped-in product credits
    // the original line item).
    const findAllMatchingGroups = useCallback((raw) => {
        const q = (raw || '').trim().toLowerCase();
        if (!q) return [];
        return groups.filter(g => {
            const parent = g.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || g.items?.[0];
            if (!parent) return false;
            const bc  = (parent.variant?.barcode || '').trim().toLowerCase();
            const sku = (parent.sku || '').trim().toLowerCase();
            if (bc && bc === q) return true;
            if (sku && sku === q) return true;
            return false;
        });
    }, [groups]);

    const handleScan = useCallback((raw) => {
        const q = (raw || '').trim();
        if (!q) return;
        // Manager Override barcode — hijack the scan, open the PIN keypad.
        if (MANAGER_BARCODE_RX.test(q.toUpperCase())) {
            setScanInput('');
            setPendingMgrBarcode(q.toUpperCase());
            return;
        }
        const matches = findAllMatchingGroups(q);
        if (matches.length === 0) {
            playError();
            triggerFlash('red');
            setScanInput('');
            setOverScanModal({
                kind: 'not_in_order',
                barcode: q,
            });
            return;
        }
        const unpressed = matches.filter(g => columnByGid[g.gid] !== 'pressed');
        if (unpressed.length === 0) {
            // Every matching bundle is already in PRESSED — over-scan.
            const matchedParent = matches[0].items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || matches[0].items?.[0];
            const vt = matchedParent?.variant?.title;
            playError();
            triggerFlash('red');
            setScanInput('');
            setOverScanModal({
                kind: 'over',
                title: matchedParent?.title || t('Item'),
                variant: (vt && vt !== 'Default Title') ? vt : null,
                sku: matchedParent?.sku || null,
                barcode: matchedParent?.variant?.barcode || q,
                totalCount: matches.length,
            });
            return;
        }
        playSuccess();
        openDetail(unpressed[0].gid, 'scan');
        setScanInput('');
    }, [findAllMatchingGroups, columnByGid, triggerFlash, openDetail]);

    // Auto-focus scan input on mount and whenever the BundleDetail modal closes
    // (moving between products). Refocus on blur for Bluetooth scanner support.
    useEffect(() => {
        if (detailGid) return; // BundleDetail open — don't fight its focus
        const el = scanRef.current;
        if (!el) return;
        const focusOnce = () => { try { el.focus(); } catch {} };
        focusOnce();
        const onBlur = () => setTimeout(focusOnce, 80);
        el.addEventListener('blur', onBlur);
        return () => el.removeEventListener('blur', onBlur);
    }, [detailGid]);

    if (loading) return <div className="ppb-loading">{t('Loading order…')}</div>;
    if (error)   return <div className="ppb-error">{error}</div>;
    if (groups.length === 0) return (
        <div className="ppb-empty">
            <div className="ppb-empty__msg">{t('No bundles to press.')}</div>
            {onBack && (
                <button className="ppb-empty__back" onClick={onBack}>
                    {t('Return to Search')}
                </button>
            )}
        </div>
    );

    return (
        <div className={`ppb${managerActive ? ' ppb--mgr-active' : ''}`}>
            {managerActive && (
                <ManagerOverrideBanner expiresAt={managerOverride.expiresAt} now={_mgrNow} />
            )}
            <style>{buildFontFaceCss(numberPreviews)}</style>
            {/* Missing required embellishments (2026-10-02, Eric) — shown above the order so the presser sees it first. */}
            <MissingEmbellishmentsKiosk missing={order?.missing_required} t={t} />
            <div className="ppb__hdr">
                <div className="ppb__hdr-left">
                    <span className="ppb__order">{order?.replacement_alias || order?.name}</span>
                    {orderClub && <span className="ppb__club">{orderClub}</span>}
                    {teamNames.length > 0 && <span className="ppb__team">{teamNames.join(' · ')}</span>}
                    {playerNames.length === 1 && <span className="ppb__players">{playerNames[0]}</span>}
                    {playerNames.length > 1 && (
                        <>
                            <span className="ppb__multi-players-badge">{t('Multiple Players')}</span>
                            <span className="ppb__players">{playerNames.join(' · ')}</span>
                        </>
                    )}
                </div>
                {notifications.length > 0 && onReviewNotifications && (
                    <button type="button" className="ppb__review-notif" onClick={onReviewNotifications}
                            title={t('Re-show this order\'s notification')}>
                        {t('Review Notification')}{notifications.length > 1 ? ` (${notifications.length})` : ''}
                    </button>
                )}
                <div className="ppb__hdr-right">
                    <div className="ppb__sort">
                        <button
                            className={`ppb__sort-btn${sortMode === 'product' ? ' ppb__sort-btn--active' : ''}`}
                            onClick={() => setSortMode('product')}
                        >{t('Product')}</button>
                        <button
                            className={`ppb__sort-btn${sortMode === 'player' ? ' ppb__sort-btn--active' : ''}`}
                            onClick={() => setSortMode('player')}
                        >{t('Player')}</button>
                    </div>
                    <button className="ppb__close-save" onClick={onBack} title={t('Close & Save Order')}>{t('Close & Save Order')}</button>
                </div>
            </div>

            <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="ppb__cols">
                    <DroppableColumn
                        id="unpressed"
                        className="ppb__col ppb__col--unpressed"
                        title={t('Unpressed')}
                        count={unpressedGids.length}
                        topAddon={
                            <div className="ppb__scan">
                                <input
                                    ref={scanRef}
                                    type="text"
                                    value={scanInput}
                                    onChange={e => setScanInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleScan(scanInput); } }}
                                    placeholder={t('Scan parent product barcode…')}
                                    autoFocus
                                    className="ppb__scan-input"
                                />
                                <button
                                    type="button"
                                    className="pkp-camera-btn"
                                    onClick={() => setCameraOpen(true)}
                                    title={t('Open camera scanner')}
                                >📷</button>
                            </div>
                        }
                        items={[
                            ...unpressedGids.map(gid => ({ kind: 'card', gid })),
                            ...referenceItems.map(g => ({ kind: 'ref', gid: g.gid, group: g })),
                        ]}
                        getKey={(it) => `${it.kind}:${it.gid}`}
                        renderItem={(it) => {
                            if (it.kind === 'ref') {
                                return (
                                    <div className="ppb__ref-card-wrap" aria-hidden="true">
                                        <BundleCard group={it.group} tpOverridesByName={tpOverridesByName} />
                                        <div className="ppb__ref-banner">{t('Pressing Not Necessary')}</div>
                                    </div>
                                );
                            }
                            const group = groups.find(g => g.gid === it.gid);
                            if (!group) return null;
                            const state = stateByGid[it.gid] || {};
                            const parent = group.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items?.[0];
                            const baseSku = getBaseSku(parent?.sku || '');
                            const isBlockedByScanOnly = userScanOnly && !!(baseSku && requireScanningBySku[baseSku]);
                            // Manager override unlocks EVERY blocked tile on
                            // this order for the 60s window. No per-SKU lock.
                            const manualBlocked = isBlockedByScanOnly && !managerActive;
                            return (
                                <DraggableCard
                                    gid={it.gid}
                                    group={group}
                                    pickStatus={state.pickStatus}
                                    onClick={manualBlocked ? undefined : () => openDetail(it.gid, 'manual')}
                                    scanOnly={manualBlocked}
                                    overrideActive={isBlockedByScanOnly && managerActive}
                                    skuNotification={parent?.sku ? skuNotifications[parent.sku] : null}
                                    tpOverridesByName={tpOverridesByName}
                                />
                            );
                        }}
                    />

                    <DroppableColumn
                        id="pressed"
                        className="ppb__col ppb__col--pressed"
                        title={t('Pressed')}
                        count={pressedGids.length}
                        items={pressedGids}
                        getKey={(gid) => gid}
                        renderItem={(gid) => {
                            const group = groups.find(g => g.gid === gid);
                            if (!group) return null;
                            const state = stateByGid[gid] || {};
                            const parentP = group.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items?.[0];
                            return (
                                <DraggableCard
                                    gid={gid}
                                    group={group}
                                    pickStatus={state.pickStatus}
                                    skuNotification={parentP?.sku ? skuNotifications[parentP.sku] : null}
                                    tpOverridesByName={tpOverridesByName}
                                />
                            );
                        }}
                    />
                </div>

                <DragOverlay>
                    {activeDragGid ? (() => {
                        const g = groups.find(grp => grp.gid === activeDragGid);
                        if (!g) return null;
                        const s = stateByGid[activeDragGid] || {};
                        const parentP = g.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || g.items?.[0];
                        return <BundleCard group={g} pickStatus={s.pickStatus}
                                           skuNotification={parentP?.sku ? skuNotifications[parentP.sku] : null}
                                           tpOverridesByName={tpOverridesByName} />;
                    })() : null}
                </DragOverlay>
            </DndContext>

            {detailGroup && (
                <BundleDetail
                    orderName={order?.replacement_alias || order?.name}
                    orderClub={orderClub}
                    group={detailGroup}
                    skuNotifications={skuNotifications}
                    bundleState={stateByGid[detailGid]}
                    numberPreviews={numberPreviews}
                    tpOverridesByName={tpOverridesByName}
                    onBack={() => setDetailGid(null)}
                    onClose={onBack}
                    onDone={() => {
                        postBundleEvent('bundle_finish', detailGid, { method: detailMethodRef.current, event_source: detailMethodRef.current });
                        setColumnByGid(prev => ({ ...prev, [detailGid]: 'pressed' }));
                        setMoveOrder(prev => [detailGid, ...prev.filter(k => k !== detailGid)]);
                        setDetailGid(null);
                        playSuccess();
                    }}
                />
            )}

            {overScanModal && (
                <div className="ppb__overscan-backdrop" onClick={() => setOverScanModal(null)}>
                    <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                        {overScanModal.kind === 'not_in_order' ? (
                            <>
                                <div className="ppb__overscan-title">{t('Not in This Order')}</div>
                                <div className="ppb__overscan-body">
                                    {t('No bundle in this order matches the scanned barcode.')}
                                    <div className="ppb__overscan-barcode">{t('Barcode:')} {overScanModal.barcode}</div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="ppb__overscan-title">{t('Already Pressed')}</div>
                                <div className="ppb__overscan-body">
                                    {overScanModal.totalCount === 1
                                        ? <>{t('This item has already been moved to')} <strong>{t('PRESSED')}</strong> {t('and the order only contains one of it.')}</>
                                        : <>{t('All')} <strong>{overScanModal.totalCount}</strong> {t('of this item have already been moved to')} <strong>{t('PRESSED')}</strong>.</>}
                                    <div className="ppb__overscan-item">{overScanModal.title}</div>
                                    {overScanModal.variant && (
                                        <div className="ppb__overscan-variant">{overScanModal.variant}</div>
                                    )}
                                    {overScanModal.sku && (
                                        <div className="ppb__overscan-sku">SKU: {overScanModal.sku}</div>
                                    )}
                                    <div className="ppb__overscan-barcode">{t('Barcode:')} {overScanModal.barcode}</div>
                                </div>
                            </>
                        )}
                        <button
                            className="ppb__overscan-ok"
                            onClick={() => { setOverScanModal(null); setTimeout(() => scanRef.current?.focus(), 50); }}
                            autoFocus
                        >{t('OK')}</button>
                    </div>
                </div>
            )}

            {flash && ReactDOM.createPortal(
                <div className={`pkp-flash pkp-flash--${flash}`} />,
                document.body
            )}

            <ManagerOverrideModal
                open={!!pendingMgrBarcode}
                barcode={pendingMgrBarcode || ''}
                fetchFn={fetchFn}
                onClose={() => setPendingMgrBarcode(null)}
                onVerified={(mgr) => {
                    setManagerOverride({ ...mgr, expiresAt: Date.now() + MANAGER_OVERRIDE_MS });
                    setPendingMgrBarcode(null);
                }}
            />

            {cameraOpen && (
                <CameraScanner
                    onDetect={(bc) => {
                        setCameraOpen(false);
                        handleScan(bc);
                        setTimeout(() => scanRef.current?.focus(), 50);
                    }}
                    onClose={() => {
                        setCameraOpen(false);
                        setTimeout(() => scanRef.current?.focus(), 50);
                    }}
                />
            )}
        </div>
    );
}
